// SEO-12 · /resources/:articleSlug — one published resource article.
//
// Path-based, prerendered, and head-owning: the page publishes its own title/description/canonical
// plus an Article + BreadcrumbList @graph (real published/modified dates from the content module —
// never a fabricated timestamp). An unknown slug renders the branded marketing 404 (noindex) rather
// than redirecting, so a dead link cannot be indexed as a soft 200.
//
// AUTHORSHIP is organisational (RESOURCE_AUTHOR). We do not invent a human byline; see
// docs/marketing/RESOURCES_EDITORIAL_PROCESS.md for the open item on named bylines.
import React, { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, CalendarDays, Clock, ExternalLink, Layers3, LifeBuoy } from "lucide-react";
import { MarketingTopBar, SiteFooter } from "../components/marketing/shared";
import { MarketingMotion } from "../components/marketing/MarketingMotion";
import { ScrollProgressBar } from "../components/marketing/scrollMotion";
import MarketingSeo, { seoOrigin } from "../components/marketing/MarketingSeo";
import MarketingNotFound from "../components/marketing/MarketingNotFound";
import ButtonLink from "../components/marketing/ButtonLink";
import { init as initAnalytics } from "../lib/analytics";
import { moduleByKey } from "./landingparts/moduleCatalog";
import {
  helpCategoriesForResource,
  helpCategoryPath,
  helpCategorySlug,
} from "./help/helpTaxonomy";
import ResourceProse from "./resources/ResourceProse";
import {
  RESOURCES_BASE_PATH,
  RESOURCE_AUTHOR,
  articleWordCount,
  relatedResources,
  resourceArticleFor,
  resourcePath,
} from "./resources/resourceLibrary";

const fmtDate = (iso) =>
  new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });

function articleSchema(article) {
  const url = `${seoOrigin}${resourcePath(article.slug)}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: article.title,
        description: article.description,
        articleSection: article.topic,
        url,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        datePublished: article.published,
        dateModified: article.updated,
        wordCount: articleWordCount(article),
        inLanguage: "en-IN",
        // Organisational author — never a fabricated person.
        author: { "@type": "Organization", name: RESOURCE_AUTHOR.name },
        publisher: { "@type": "Organization", name: "Vidyapeeth360" },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${seoOrigin}/` },
          { "@type": "ListItem", position: 2, name: "Resources", item: `${seoOrigin}${RESOURCES_BASE_PATH}` },
          { "@type": "ListItem", position: 3, name: article.title, item: url },
        ],
      },
    ],
  };
}

function ProductLinks({ article }) {
  const modules = (article.moduleLinks || []).map((key) => moduleByKey[key]).filter(Boolean);
  if (!modules.length) return null;
  return (
    <aside
      className="mt-12 rounded-2xl border border-line bg-surface p-6"
      aria-labelledby="resource-product-links"
      data-testid="resource-module-links"
    >
      {/* PROD-040: one name for one region — the visible label IS the heading, so a
          screen-reader user and a sighted reader are given the same section name. */}
      <h2
        id="resource-product-links"
        className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.12em] text-accent"
      >
        <Layers3 className="h-4 w-4" aria-hidden="true" /> Where this lives in the product
      </h2>
      <ul className="mt-4 grid gap-3 sm:grid-cols-2">
        {modules.map((mod) => (
          <li key={mod.key}>
            <Link
              to={`/modules/${mod.key}`}
              data-testid={`resource-module-${mod.key}`}
              className="group flex h-full flex-col rounded-xl border border-line bg-canvas p-4 transition-[border-color,box-shadow] duration-150 hover:border-accent/40 hover:shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <span className="font-heading text-[15px] font-semibold text-ink transition-colors group-hover:text-accent">
                {mod.name}
              </span>
              {mod.oneLiner ? (
                <span className="mt-1 text-[13px] leading-relaxed text-ink-2">{mod.oneLiner}</span>
              ) : null}
            </Link>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// Cross-discovery, contextual rather than generic. The Help Center (platform-admin-owned CMS
// content) and this library (code-authored) stay separate systems; what links them is data the
// article ALREADY declares — its module interlinks and its topic — resolved through the ONE
// taxonomy bridge in pages/help/helpTaxonomy.js. No per-article list to keep in sync: a new guide
// gets its hand-off from its own `moduleLinks`, and a subject with no public help category gets
// the honest generic link instead of a deep link into an empty filter.
function HelpCenterLinks({ article }) {
  const categories = helpCategoriesForResource(article, (key) => moduleByKey[key]?.group);
  return (
    <aside
      className="mt-8 rounded-2xl border border-line bg-surface p-6"
      aria-labelledby="resource-help-links"
      data-testid="resource-help-links"
    >
      <h2
        id="resource-help-links"
        className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.12em] text-accent"
      >
        <LifeBuoy className="h-4 w-4" aria-hidden="true" /> How to do this in the product
      </h2>
      <p className="mt-2 max-w-2xl text-[14px] leading-[1.7] text-ink-2">
        This guide is about the decision. The Help Center covers the steps —{" "}
        {categories.length ? "start with these topics:" : "browse the full knowledge base:"}
      </p>
      <ul className="mt-4 flex flex-wrap gap-2.5">
        {categories.map((category) => (
          <li key={category}>
            <Link
              to={helpCategoryPath(category)}
              data-testid={`resource-help-category-${helpCategorySlug(category)}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas px-3.5 py-1.5 text-[13px] font-semibold text-ink-2 transition-colors hover:border-accent/50 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              {category} <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
            </Link>
          </li>
        ))}
        <li>
          <Link
            to="/help"
            data-testid="resource-help-index-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas px-3.5 py-1.5 text-[13px] font-semibold text-ink-2 transition-colors hover:border-accent/50 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          >
            All Help Center guides <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </li>
      </ul>
    </aside>
  );
}

function Sources({ sources }) {
  if (!sources?.length) return null;
  return (
    <section className="mt-11" aria-labelledby="resource-sources" data-testid="resource-sources">
      <h2 id="resource-sources" className="font-heading text-[22px] font-bold tracking-tight text-ink sm:text-[26px]">
        Primary sources
      </h2>
      <ul className="mt-4 space-y-3">
        {sources.map((source) => (
          <li key={source.href} className="text-[15px] leading-relaxed text-ink-2">
            <a
              href={source.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-baseline gap-1.5 font-semibold text-accent-active underline decoration-current/30 underline-offset-2 hover:decoration-current rounded focus:outline-none focus-visible:ring-2 ring-primary"
            >
              {source.label}
              <ExternalLink className="h-3.5 w-3.5 self-center" aria-hidden="true" />
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
            {source.note ? <span className="block text-[13.5px] text-ink-subtle">{source.note}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ResourceArticlePageContent() {
  const { articleSlug } = useParams();
  const article = resourceArticleFor(articleSlug);

  useEffect(() => {
    initAnalytics();
    window.scrollTo(0, 0);
  }, [articleSlug]);

  const schema = useMemo(() => (article ? articleSchema(article) : null), [article]);
  const related = useMemo(() => relatedResources(article), [article]);

  if (!article) return <MarketingNotFound />;

  const seoTitle = article.seoTitle || `${article.title} | Vidyapeeth360`;

  return (
    <div
      className="landing-page flex min-h-screen flex-col bg-canvas text-ink"
      data-testid="resource-article-page"
      data-resource-slug={article.slug}
    >
      <MarketingSeo
        title={seoTitle}
        description={article.description}
        path={resourcePath(article.slug)}
        type="article"
        schema={schema}
      />
      <MarketingTopBar />
        <ScrollProgressBar />

      <main id="main" className="flex-1">
        <article className="mx-auto max-w-3xl px-5 pb-4 pt-10 sm:pt-14">
          <nav aria-label="Breadcrumb" className="text-[12.5px] text-ink-subtle">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to={RESOURCES_BASE_PATH} className="inline-flex items-center gap-1.5 font-semibold text-accent hover:underline rounded focus:outline-none focus-visible:ring-2 ring-primary" data-testid="resource-breadcrumb-index">
                  <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Resources
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-ink-2">{article.topic}</li>
            </ol>
          </nav>

          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-[12.5px] font-semibold text-ink-2">
            <BookOpen className="h-3.5 w-3.5 text-primary" aria-hidden="true" /> {article.topic}
          </p>
          <h1 className="mt-4 font-heading text-[30px] font-bold leading-[1.1] tracking-tight text-ink sm:text-[40px]">
            {article.title}
          </h1>
          <p className="mt-4 text-[17px] leading-[1.75] text-ink-2 sm:text-[18.5px]" data-testid="resource-standfirst">
            {article.standfirst}
          </p>

          <div
            className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-line py-3.5 text-[12.5px] text-ink-subtle"
            data-testid="resource-byline"
          >
            <span className="font-semibold text-ink-2">{RESOURCE_AUTHOR.name}</span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
              Published <time dateTime={article.published}>{fmtDate(article.published)}</time>
            </span>
            {article.updated !== article.published ? (
              <span className="inline-flex items-center gap-1.5">
                Updated <time dateTime={article.updated}>{fmtDate(article.updated)}</time>
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {article.readingMinutes} min read
            </span>
          </div>

          <ResourceProse sections={article.sections} />
          <Sources sources={article.sources} />
          <ProductLinks article={article} />
          <HelpCenterLinks article={article} />
        </article>

        <section className="mx-auto max-w-3xl px-5 pb-6 pt-10" aria-labelledby="resource-conversion-heading">
          <div className="rounded-2xl border border-line bg-surface p-6 sm:p-7" data-testid="resource-conversion">
            <p className="text-[12px] font-bold uppercase tracking-[0.13em] text-accent">Next step</p>
            <h2
              id="resource-conversion-heading"
              className="mt-2 font-heading text-[21px] font-bold leading-snug tracking-tight text-ink sm:text-[25px]"
            >
              {article.conversion.label}
            </h2>
            <p className="mt-2 text-[14px] leading-[1.7] text-ink-2">{article.conversion.note}</p>
            <ButtonLink to={article.conversion.to} size="lg" className="mt-5" data-testid="resource-conversion-cta">
              {article.conversion.label} <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </ButtonLink>
          </div>
        </section>

        {related.length ? (
          <section className="mx-auto max-w-6xl px-5 py-12 sm:py-14" aria-labelledby="resource-related-heading">
            <h2
              id="resource-related-heading"
              className="font-heading text-[20px] font-bold tracking-tight text-ink sm:text-[24px]"
            >
              Keep reading
            </h2>
            <ul className="mt-5 grid gap-4 md:grid-cols-3" data-testid="resource-related">
              {related.map((item) => (
                <li key={item.slug} className="flex">
                  <Link
                    to={resourcePath(item.slug)}
                    data-testid={`resource-related-${item.slug}`}
                    className="group flex w-full flex-col rounded-2xl border border-line bg-surface p-5 transition-[border-color,box-shadow] duration-150 hover:border-accent/40 hover:shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                  >
                    <span className="text-[11.5px] font-bold uppercase tracking-[0.12em] text-accent">{item.topic}</span>
                    <span className="mt-2 font-heading text-[16px] font-semibold leading-snug text-ink transition-colors group-hover:text-accent">
                      {item.title}
                    </span>
                    <span className="mt-2 text-[13px] leading-relaxed text-ink-2">{item.description}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>

      <SiteFooter />
    </div>
  );
}

export default function ResourceArticlePage() {
  return (
    <MarketingMotion>
      <ResourceArticlePageContent />
    </MarketingMotion>
  );
}
